import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { 
  BarChart3, TrendingUp, Target, Zap, AlertTriangle, DollarSign,
  CheckCircle, Star, ArrowRight, Play, PieChart, Users, 
  FileText, Lightbulb, Shield, ArrowDown, ArrowUp, Clock
} from "lucide-react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import AwardsSection from "@/components/trust/AwardsSection";

const DataToAction = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stats = [
    { value: "3.2x", label: "Higher ROI on people programs", color: "text-green-600" },
    { value: "45%", label: "Reduction in turnover", color: "text-blue-600" },
    { value: "68%", label: "Faster time to action", color: "text-purple-600" },
    { value: "$2.1M", label: "Average annual savings", color: "text-orange-600" }
  ];

  const features = [
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: "Predictive Analytics",
      description: "AI-powered insights predict turnover risk, identify top performers, and reveal engagement patterns before they become problems."
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "ROI Measurement",
      description: "Track the business impact of every recognition, engagement initiative, and manager development program with clear metrics."
    },
    {
      icon: <AlertTriangle className="h-8 w-8" />,
      title: "Early Warning System",
      description: "Get alerts when engagement drops, performance flags emerge, or flight risk increases - before it's too late."
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Performance Correlation",
      description: "Understand what drives top performance in your organization and replicate those conditions across teams."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Team Health Scoring",
      description: "Get instant visibility into team dynamics, collaboration effectiveness, and overall organizational health."
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: "Action Recommendations",
      description: "Don't just see the data - get specific, actionable recommendations to improve engagement and performance."
    }
  ];

  const testimonials = [
    {
      name: "Jennifer Martinez",
      role: "Chief People Officer, ScaleUp Inc",
      quote: "Assembly's insights helped us reduce turnover by 45% and saved us $1.8M in recruitment costs. The ROI was clear within 6 months.",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face"
    },
    {
      name: "David Kim",
      role: "VP of Operations, TechFlow",
      quote: "We went from gut-feeling decisions to data-driven people strategies. Our employee satisfaction scores improved 3x faster than industry average.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face"
    }
  ];

  const beforeAfter = [
    {
      before: "Reactive to turnover after it happens",
      after: "Predict and prevent turnover 6 months in advance"
    },
    {
      before: "Guess which programs work",
      after: "Track ROI of every people initiative"
    },
    {
      before: "Annual engagement surveys only",
      after: "Real-time pulse on team health"
    },
    {
      before: "One-size-fits-all solutions",
      after: "Personalized action plans per team"
    }
  ];

  return (
    <ProductPageLayout
      title="Turn Data Into Action - People Analytics That Drive Results"
      description="Stop guessing about employee engagement. Get AI-powered insights that predict turnover, measure ROI, and provide actionable recommendations for better business outcomes."
      keywords="people analytics, employee engagement data, turnover prediction, HR analytics, workforce insights, employee retention"
      ctaTitle="Ready to Make Data-Driven People Decisions?"
      ctaDescription="Join 300+ companies using Assembly's analytics to reduce turnover and increase ROI"
      ctaButtonText="See Your Data in Action"
      ctaVariant="green"
      showCTA={false}
      onCtaClick={() => setIsDemoModalOpen(true)}
    >
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <Badge className="mb-6 bg-green-100 text-green-700 hover:bg-green-100 border-green-200 text-sm font-medium px-4 py-2">
                PREDICTIVE ANALYTICS
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Turn People Data Into
                <br />
                <span className="text-green-600">Business Results</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg leading-relaxed">
                Stop reactive people management. Get AI-powered insights that predict turnover, measure ROI, and provide clear action plans for 3.2x better results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-medium text-lg"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-yellow-400 fill-current" />
                  <span className="font-medium">4.9/5</span>
                </div>
                <span>•</span>
                <span>300+ companies</span>
                <span>•</span>
                <span>$2.1M avg. savings</span>
              </div>
            </motion.div>

            {/* Analytics Dashboard Mockup */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-8 relative max-w-md ml-auto">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">People Analytics</h3>
                    <p className="text-gray-500 text-sm">Real-time insights</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium text-red-700">High Risk</span>
                    </div>
                    <div className="text-2xl font-bold text-red-600">3</div>
                    <div className="text-xs text-red-600">Team members</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-700">ROI</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">3.2x</div>
                    <div className="text-xs text-green-600">vs. industry</div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-3">Recommended Actions</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-2 bg-orange-50 rounded border-l-4 border-orange-400">
                      <Target className="h-4 w-4 text-orange-500" />
                      <span className="text-sm">Schedule 1:1 with Sarah (85% flight risk)</span>
                    </div>
                    <div className="flex items-center gap-3 p-2 bg-blue-50 rounded border-l-4 border-blue-400">
                      <Users className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">Recognize Marketing team's performance</span>
                    </div>
                  </div>
                </div>

                <Button size="sm" className="w-full bg-green-600 hover:bg-green-700 text-white">
                  View Full Report
                </Button>
                
                <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg">
                  <PieChart className="h-6 w-6 text-white" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12 sm:mb-16">
            <motion.h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6" {...fadeInUp}>
              You'll be in good company.
            </motion.h2>
            <motion.p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto" {...fadeInUp}>Many top organizations leverage Assembly as a Digital HQ to support their transition to hybrid work and nurture a positive workplace culture.</motion.p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center opacity-60">
            {[{
            name: "StubHub",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393801e8e212d1d42ff_stubhub-gray.svg"
          }, {
            name: "1-800-Flowers",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/6386539332627ece447341d4_1800flowers-gray.svg"
          }, {
            name: "Coca-Cola",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/638653928ded58181120ad86_Coca-Cola-gray.svg"
          }, {
            name: "Amazon",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393cd30f0e52523af12_Amazon-gray.svg"
          }, {
            name: "PODS",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393ed49964f0f60c3c7_PODS-gray.svg"
          }, {
            name: "Allbirds",
            src: "https://cdn.prod.website-files.com/6058ba87eec5717595807524/63865393708a2292ab2159f4_Allbirds-gray.svg"
          }].map((logo, index) => <motion.div key={index} className="flex items-center justify-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }}>
                <img src={logo.src} alt={`${logo.name} logo`} className="h-8 w-auto object-contain" />
              </motion.div>)}
          </div>
          
          <div className="mt-12 sm:mt-16">
            <AwardsSection />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="space-y-2" 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={`text-4xl font-bold ${stat.color}`}>{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Stop Flying Blind on People Decisions
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Companies lose $15,000 per voluntary quit, yet 87% of HR leaders admit they're making people decisions based on gut feeling rather than data.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto mb-12">
            <motion.div 
              className="bg-red-50 border-2 border-red-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">😵‍💫</span>
                </div>
                <h3 className="text-2xl font-bold text-red-800">Without Data</h3>
              </div>
              <ul className="space-y-4 text-red-700">
                {beforeAfter.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowDown className="text-red-500 mt-1 h-5 w-5 flex-shrink-0" />
                    <span className="text-lg">{item.before}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="bg-green-50 border-2 border-green-200 rounded-2xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-2xl font-bold text-green-800">With Assembly Analytics</h3>
              </div>
              <ul className="space-y-4 text-green-700">
                {beforeAfter.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <ArrowUp className="text-green-500 mt-1 h-5 w-5 flex-shrink-0" />
                    <span className="text-lg">{item.after}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4"
              onClick={() => setIsDemoModalOpen(true)}
            >
              See Your Organization's Data
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Calculate Your Potential Savings
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              See how much Assembly's analytics could save your organization
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <Card className="p-8 text-center border-blue-200 bg-blue-50">
                <DollarSign className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-blue-600 mb-2">$487K</div>
                <div className="font-medium text-blue-800 mb-2">Turnover Cost Reduction</div>
                <div className="text-sm text-blue-600">Based on 45% reduction in voluntary quits</div>
              </Card>
              <Card className="p-8 text-center border-green-200 bg-green-50">
                <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-green-600 mb-2">$1.2M</div>
                <div className="font-medium text-green-800 mb-2">Productivity Gains</div>
                <div className="text-sm text-green-600">From improved engagement and performance</div>
              </Card>
              <Card className="p-8 text-center border-purple-200 bg-purple-50">
                <Clock className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <div className="text-3xl font-bold text-purple-600 mb-2">$320K</div>
                <div className="font-medium text-purple-800 mb-2">Time Savings</div>
                <div className="text-sm text-purple-600">Automated insights vs. manual analysis</div>
              </Card>
            </div>

            <div className="text-center bg-gradient-to-r from-green-600 to-teal-600 text-white p-8 rounded-2xl">
              <div className="text-4xl font-bold mb-2">$2.1M</div>
              <div className="text-xl mb-4">Total Annual ROI</div>
              <Button 
                size="lg" 
                className="bg-white text-green-700 hover:bg-gray-100 px-8 py-4"
                onClick={() => setIsDemoModalOpen(true)}
              >
                Get Your Custom ROI Report
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Analytics That Actually Drive Action
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Don't just collect data - get insights that lead to measurable business outcomes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-lg transition-all hover:border-green-200"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <motion.div className="text-center mb-16" {...fadeInUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Connect Your Data Sources
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Pull insights from your HRIS and analytics tools, or export reports for deeper analysis
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-4xl mx-auto">
            <motion.div 
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BarChart3 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">HRIS Analytics Integration</h3>
              </div>
              <p className="text-gray-600 mb-6 text-center">
                Combine people data from your HR system with engagement metrics for comprehensive insights
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Performance correlation</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Turnover prediction</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-gray-700">Real-time data sync</span>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">API & Export Options</h3>
              </div>
              <p className="text-gray-600 mb-6 text-center">
                Access your data through our API or download detailed reports for custom analysis
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-gray-700">REST API access</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-gray-700">Custom report exports</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-teal-600" />
                  <span className="text-gray-700">Automated data delivery</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Stop Guessing. Start Knowing.
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
              Get AI-powered people insights that predict problems, measure ROI, and provide clear action plans. 
              See your custom analytics in a 15-minute demo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="bg-white text-green-700 hover:bg-gray-100 px-8 py-4 font-medium text-lg"
                onClick={() => setIsDemoModalOpen(true)}
              >
                See Your Data Live
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  <span>Custom ROI report</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="h-4 w-4" />
                  <span>Live demo of your data</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
    </ProductPageLayout>
  );
};

export default DataToAction;